import { z } from 'zod'
import type { ApiClient } from './client'

/**
 * Suppression rules — the legacy "false positive rules".
 *
 * Path says falsepositive; the product concept is SUPPRESSION: a rule that
 * stops a detection (workbook) from emitting signals for a slice of
 * entities, without disabling the detection. Terms are AND of ORs:
 *
 *   rule = { '<Suspect|Target><Field>': [values…] }
 *   values within a key are OR'd; keys are AND'd.
 *
 * Distinct concept, same legacy screen family: "Tag False Positive"
 * (/api/signals/add_rule) flags individual signals — NOT modelled here.
 *
 * ⚠️ Shapes UNVERIFIED — reconstructed 2026-08-22 from legacy source
 * (src/services/falsepositive.js, src/pages/system/falsepositive/index.js).
 * No live capture exists for /api/falsepositive/* yet; capture before live
 * mode ships. Legacy quirks preserved on purpose:
 *   - add takes `rules: [rule]` (plural), update takes `rule` (singular)
 *   - list returns FLAT rows, one per rule, workbook fields denormalized on
 *   - showrule is a SECOND read: list rows may omit/abbreviate `rule`, the
 *     full expression comes from showrule (documented backend contract)
 *   - last_update_at is EPOCH SECONDS
 */

/** AND of ORs. Key shape: '<Suspect|Target><Field>', e.g. 'SuspectHost'. */
export type SuppressionTerms = Record<string, string[]>

const terms = z.record(z.string(), z.array(z.string())).default({})

export const suppressionRule = z
  .object({
    rule_id: z.union([z.string(), z.number()]).transform(String),
    wb_id: z.union([z.string(), z.number()]).transform(String),
    scope_id: z.string().default(''),
    /** Workbook (detection) name, denormalized onto the rule row. */
    name: z.string().default(''),
    /** Workbook folder path. */
    path: z.string().default(''),
    rule: terms,
    is_enable: z.boolean().default(false),
    owner_id: z.string().nullable().default(null),
    /** Epoch seconds. */
    last_update_at: z.number().nullable().default(null),
  })
  .passthrough()

export const suppressionListResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.array(suppressionRule).default([]),
})

export const suppressionShowRuleResponse = z.object({
  status: z.string().optional(),
  data: terms,
})

const mutationResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
})

export type SuppressionRule = z.output<typeof suppressionRule>

/* ----------------------------------------------------------------- helpers */

/** 'SuspectHost' -> { role: 'Suspect', field: 'Host' }. Legacy concat, no
 * separator — the prefix test is the only safe parse. */
export function splitTermKey(key: string): { role: 'Suspect' | 'Target' | 'Other'; field: string } {
  if (key.startsWith('Suspect')) return { role: 'Suspect', field: key.slice('Suspect'.length) }
  if (key.startsWith('Target')) return { role: 'Target', field: key.slice('Target'.length) }
  return { role: 'Other', field: key }
}

export function makeTermKey(role: 'Suspect' | 'Target', field: string): string {
  return role + field
}

/** Dedupe + trim values, drop empty keys — the shape the API expects. */
export function normalizeTerms(t: SuppressionTerms): SuppressionTerms {
  const out: SuppressionTerms = {}
  for (const [k, vals] of Object.entries(t)) {
    const clean = [...new Set(vals.map((v) => v.trim()).filter(Boolean))]
    if (k.trim() && clean.length) out[k.trim()] = clean
  }
  return out
}

/** ms epoch for display/sort; null when the row has no timestamp. */
export function ruleUpdatedAtMs(r: Pick<SuppressionRule, 'last_update_at'>): number | null {
  return r.last_update_at !== null && r.last_update_at > 0 ? r.last_update_at * 1000 : null
}

/* --------------------------------------------------------------------- api */

export interface SuppressionScope {
  clusterId: string
  scopeId: string
}

export interface SuppressionApi {
  list: (q: SuppressionScope & { pageNo?: number; pageSize?: number }) => Promise<SuppressionRule[]>
  /** The full expression for one rule — the second read. */
  show: (q: SuppressionScope & { ruleId: string }) => Promise<SuppressionTerms>
  add: (q: SuppressionScope & { wbId: string; terms: SuppressionTerms }) => Promise<void>
  update: (
    q: SuppressionScope & { wbId: string; ruleId: string; terms: SuppressionTerms; enabled: boolean },
  ) => Promise<void>
  remove: (q: SuppressionScope & { wbId: string; ruleId: string }) => Promise<void>
}

export function createSuppressionApi(client: ApiClient): SuppressionApi {
  return {
    list: async ({ clusterId, scopeId, pageNo = 1, pageSize = 1000 }) => {
      const res = await client.request('POST', `/${clusterId}/api/falsepositive/rules/list`, {
        body: { scope_id: scopeId, pageno: pageNo, pagesize: pageSize },
        schema: suppressionListResponse,
      })
      return res.data
    },

    show: async ({ clusterId, scopeId, ruleId }) => {
      const res = await client.request('POST', `/${clusterId}/api/falsepositive/rules/showrule`, {
        body: { scope_id: scopeId, rule_id: ruleId },
        schema: suppressionShowRuleResponse,
      })
      return res.data
    },

    add: async ({ clusterId, scopeId, wbId, terms: t }) => {
      // Legacy always creates enabled; the plural `rules` wrapping one rule
      // is the wire contract, not a mistake here.
      await client.request('POST', `/${clusterId}/api/falsepositive/rules/add`, {
        body: { scope_id: scopeId, wb_id: wbId, rules: [normalizeTerms(t)], is_enable: true },
        schema: mutationResponse,
      })
    },

    update: async ({ clusterId, scopeId, wbId, ruleId, terms: t, enabled }) => {
      await client.request('POST', `/${clusterId}/api/falsepositive/rules/update`, {
        body: {
          scope_id: scopeId,
          wb_id: wbId,
          rule_id: ruleId,
          rule: normalizeTerms(t),
          is_enable: enabled,
        },
        schema: mutationResponse,
      })
    },

    remove: async ({ clusterId, scopeId, wbId, ruleId }) => {
      await client.request('POST', `/${clusterId}/api/falsepositive/rules/delete`, {
        body: { scope_id: scopeId, wb_id: wbId, rule_id: ruleId },
        schema: mutationResponse,
      })
    },
  }
}
